import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface TopAdvisor {
  advisorId: string;
  agentName: string | null;
  opportunityCount: number;
  avgScore: number;
  totalEstimatedValue: number;
}

export interface ScoreHistogramBucket {
  bucket: string;
  min: number;
  max: number;
  count: number;
}

export interface OpportunitiesOverTime {
  period: string;
  count: number;
}

export interface StatusFunnel {
  status: string;
  count: number;
}

export interface CarrierBreakdown {
  carrierName: string;
  count: number;
  avgScore: number;
  totalEstimatedValue: number;
}

export interface ProductTypeBreakdown {
  productType: string;
  count: number;
  avgScore: number;
}

export interface FactorDistribution {
  factorName: string;
  avgScore: number;
  passedCount: number;
  failedCount: number;
  unknownCount: number;
}

export interface AnalyticsSummary {
  totalOpportunities: number;
  avgScore: number;
  totalEstimatedValue: number;
  avgEstimatedValue: number;
  avgAccountValue: number;
  avgPolicyAge: number;
}

export interface OpportunityAnalytics {
  summary: AnalyticsSummary;
  topAdvisors: TopAdvisor[];
  scoreHistogram: ScoreHistogramBucket[];
  opportunitiesOverTime: OpportunitiesOverTime[];
  statusFunnel: StatusFunnel[];
  carrierBreakdown: CarrierBreakdown[];
  productTypeBreakdown: ProductTypeBreakdown[];
  factorDistribution: FactorDistribution[];
}

// ============================================================================
// Query Keys
// ============================================================================

export const analyticsKeys = {
  all: ['analytics'] as const,
  data: (filters?: { advisorId?: string }) => [...analyticsKeys.all, 'data', filters] as const,
  summary: (filters?: { advisorId?: string }) =>
    [...analyticsKeys.all, 'summary', filters] as const,
  scoreDistribution: (filters?: { advisorId?: string }) =>
    [...analyticsKeys.all, 'score-distribution', filters] as const,
  statusFunnel: (filters?: { advisorId?: string }) =>
    [...analyticsKeys.all, 'status-funnel', filters] as const,
  carriers: (filters?: { advisorId?: string }) =>
    [...analyticsKeys.all, 'carriers', filters] as const,
  advisors: (filters?: { advisorId?: string }) =>
    [...analyticsKeys.all, 'advisors', filters] as const,
  timeline: (filters?: { advisorId?: string }) =>
    [...analyticsKeys.all, 'timeline', filters] as const,
};

// ============================================================================
// API
// ============================================================================

async function fetchAnalytics(filters?: { advisorId?: string }): Promise<OpportunityAnalytics> {
  const params = new URLSearchParams();
  if (filters?.advisorId) params.set('advisorId', filters.advisorId);
  const qs = params.toString();
  const url = qs ? `/opportunities/analytics?${qs}` : '/opportunities/analytics';
  const response = await apiClient.get<OpportunityAnalytics>(url);
  return response.data;
}

function buildAnalyticsUrl(path: string, filters?: { advisorId?: string }) {
  const params = new URLSearchParams();
  if (filters?.advisorId) params.set('advisorId', filters.advisorId);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function fetchAnalyticsSummary(filters?: { advisorId?: string }): Promise<AnalyticsSummary> {
  const response = await apiClient.get<AnalyticsSummary>(
    buildAnalyticsUrl('/opportunities/analytics/summary', filters)
  );
  return response.data;
}

async function fetchScoreDistribution(filters?: {
  advisorId?: string;
}): Promise<ScoreHistogramBucket[]> {
  const response = await apiClient.get<ScoreHistogramBucket[]>(
    buildAnalyticsUrl('/opportunities/analytics/score-distribution', filters)
  );
  return response.data;
}

async function fetchStatusFunnel(filters?: { advisorId?: string }): Promise<StatusFunnel[]> {
  const response = await apiClient.get<StatusFunnel[]>(
    buildAnalyticsUrl('/opportunities/analytics/status-funnel', filters)
  );
  return response.data;
}

async function fetchCarrierBreakdown(filters?: {
  advisorId?: string;
}): Promise<CarrierBreakdown[]> {
  const response = await apiClient.get<CarrierBreakdown[]>(
    buildAnalyticsUrl('/opportunities/analytics/carriers', filters)
  );
  return response.data;
}

async function fetchTopAdvisors(filters?: { advisorId?: string }): Promise<TopAdvisor[]> {
  const response = await apiClient.get<TopAdvisor[]>(
    buildAnalyticsUrl('/opportunities/analytics/advisors', filters)
  );
  return response.data;
}

// ============================================================================
// Hook
// ============================================================================

export function useAnalytics(
  filters?: { advisorId?: string },
  options?: Omit<UseQueryOptions<OpportunityAnalytics>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: analyticsKeys.data(filters),
    queryFn: () => fetchAnalytics(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useAnalyticsSummary(
  filters?: { advisorId?: string },
  options?: Omit<UseQueryOptions<AnalyticsSummary>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: analyticsKeys.summary(filters),
    queryFn: () => fetchAnalyticsSummary(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useScoreDistribution(
  filters?: { advisorId?: string },
  options?: Omit<UseQueryOptions<ScoreHistogramBucket[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: analyticsKeys.scoreDistribution(filters),
    queryFn: () => fetchScoreDistribution(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useStatusFunnel(
  filters?: { advisorId?: string },
  options?: Omit<UseQueryOptions<StatusFunnel[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: analyticsKeys.statusFunnel(filters),
    queryFn: () => fetchStatusFunnel(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCarrierBreakdown(
  filters?: { advisorId?: string },
  options?: Omit<UseQueryOptions<CarrierBreakdown[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: analyticsKeys.carriers(filters),
    queryFn: () => fetchCarrierBreakdown(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useTopAdvisors(
  filters?: { advisorId?: string },
  options?: Omit<UseQueryOptions<TopAdvisor[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: analyticsKeys.advisors(filters),
    queryFn: () => fetchTopAdvisors(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}
